// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionTitleStyles } from "@werner/App/Components/Common/SectionTitle";
import { TooltipPresentationProps } from "@werner/MobiusOverrides/Tooltip/Tooltip";
import HelpCircle from "@werner/XDL/Icons/Question";
import { color, xScreen } from "@werner/XDL/Utilities";
import { css } from "styled-components";

export interface XCheckoutPageInnerContainerStyles {
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
}

export const xCheckoutPageInnerContainerStyles: XCheckoutPageInnerContainerStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 20px;
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
};

export const datepickerHintStyles: TooltipPresentationProps = {
    iconProps: { src: HelpCircle, color: "primary", size: 12 },
};

export const securityCodeHelpLink: LinkPresentationProps = {
    icon: {
        iconProps: {
            src: HelpCircle,
            size: 12,
            color: "text.link",
            css: css`
                margin-right: 0;
            `,
        },
    },
};

export const securityCodeTextInput: TextFieldPresentationProps = {
    cssOverrides: {
        formField: css`
            position: relative;
            .hint-wrapper {
                display: flex;
            }
        `,
        formInputWrapper: css`
            display: flex;
            align-items: flex-end;
            flex-direction: column;
        `,
    },
};

export const checkoutReviewLabelValueWrapper: InjectableCss = {
    css: css`
        display: flex;
        margin-bottom: 12px;
    `,
};

export const checkoutReviewValue: TypographyProps = {
    size: 15,
    weight: 400,
    css: css`
        letter-spacing: 0;
        line-height: 17px;
        margin-left: 4px;
    `,
};
export const checkoutReviewLabel: TypographyProps = {
    size: 15,
    weight: "bold",
    css: css`
        letter-spacing: 0;
        line-height: 17px;
        white-space: nowrap;
        &:after {
            content: ":";
        }
    `,
};

export const checkoutReviewSectionTitle: SectionTitleStyles = {
    title: {
        size: 32,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
        `,
    },
};

export const xAddressTitleStyles: TypographyProps = {
    size: 22,
    weight: 500,
    css: css`
        letter-spacing: 0;
        line-height: 32px;
    `,
};

interface CheckoutReivewInfoStyles {
    container?: GridContainerProps;
    sectionGridItem?: GridItemProps;
    sectionGridContainer?: GridContainerProps;
    sectionColumn?: GridItemProps;
}

export const checkoutReivewInfoStyles: CheckoutReivewInfoStyles = {
    container: { gap: 70 },
    sectionGridItem: {
        width: 12,
        css: css`
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    sectionGridContainer: { gap: 20 },
    sectionColumn: {
        width: [12, 12, 6, 4, 4],
        css: css`
            flex-direction: column;
            gap: 8px;
        `,
    },
};
